<template>
  <Tpl.define v-slot="{ subsection, active, disabled, iconRight }">
    <StellaTooltip :text="shrink ? subsection.label : undefined">
      <div
        class="flex h-10 px-3.5 gap-2 rounded-md bg-cream-10 items-center group"
        :class="[
          active && 'bg-cream-40',
          !disabled && 'cursor-pointer  hover:bg-cream-40',
          shrink ? 'w-12 shrink-0' : 'w-full',
        ]"
      >
        <div class="flex flex-row items-center gap-4">
          <StellaIcon
            v-if="subsection.iconId"
            :icon="subsection.iconId"
            class="shrink-0"
          />
          <StellaText v-if="!shrink" type="Label XS" class="truncate">
            {{ subsection.label }}
          </StellaText>
        </div>
        <template v-if="iconRight && !shrink">
          <Spacer />
          <StellaIcon
            :icon="iconRight"
            class="opacity-0 group-hover:opacity-100 shrink-0"
          />
        </template>
      </div>
    </StellaTooltip>
  </Tpl.define>

  <div
    class="bg-cream-10 isolate h-full shadow-lg flex flex-col text-grey-80 z-10"
    :class="[
      !inline && 'fixed top-0 rounded-tr-lg',
      shrink ? 'w-[80px]' : 'w-[17rem]',
    ]"
  >
    <div
      v-if="!inline"
      class="w-full flex justify-between items-center px-4 pt-4 mb-2"
    >
      <Link to="/">
        <StellaImage class="h-10" :src="logo" />
      </Link>
      <StellaIcon
        @click="emit('close')"
        icon="x"
        size="md"
        class="cursor-pointer"
      />
    </div>
    <div
      class="w-full h-4 relative top-0 z-20 from-grey-10 shrink-0"
      :class="topShadow.state ? 'bg-gradient-to-b' : 'bg-transparent'"
    />
    <div
      class="flex flex-col grow overflow-y-auto overflow-x-hidden -mt-4"
      :class="[inline && 'pt-4']"
    >
      <StellaEdgeObserver
        @reachHead="topShadow.off"
        @leaveHead="topShadow.on"
        @reachTail="bottomShadow.off"
        @leaveTail="bottomShadow.on"
        direction="vertical"
        class="flex flex-col w-full grow"
      >
        <ul class="flex flex-col px-4">
          <li
            v-for="section of navSections"
            :key="section.id"
            class="border-b last:border-none border-grey-20 py-1 min-w-12"
          >
            <ul>
              <StellaSpinner
                v-if="section.loading"
                size="xs"
                class="my-2 mx-4"
              />
              <li
                v-for="subsection of section.subsections"
                :key="subsection.id"
                class="relative isolate"
              >
                <router-link
                  v-if="'route' in subsection"
                  :to="subsection.route"
                  @click="subsection.action"
                  v-track:mousedown="'Nav Sub Item Clicked'"
                  v-track-props="{
                    component: 'Side Nav',
                    navItem: section.id,
                    navSubItem: subsection.label,
                  }"
                >
                  <Tpl.reuse
                    :subsection="subsection"
                    :active="
                      (route.name === subsection.route.name ||
                        route.path.split('/')[1] === subsection.route.name) &&
                      route.params?.nickname ===
                        subsection.route.params?.nickname
                    "
                  />
                </router-link>
                <a
                  v-else-if="'url' in subsection"
                  :href="subsection.url"
                  @click="subsection.action"
                  target="_blank"
                  v-track:mousedown="'Nav Sub Item Clicked'"
                  v-track-props="{
                    component: 'Side Nav',
                    navItem: section.id,
                    navSubItem: subsection.label,
                  }"
                >
                  <Tpl.reuse :subsection="subsection" :iconRight="'newtab'" />
                </a>
                <div
                  v-else
                  @click="
                    'action' in subsection ? subsection.action() : undefined
                  "
                  v-track:mousedown="'Nav Sub Item Clicked'"
                  v-track-props="{
                    component: 'Side Nav',
                    navItem: section.id,
                    navSubItem: subsection.label,
                  }"
                >
                  <Tpl.reuse
                    :subsection="subsection"
                    :disabled="!('action' in subsection)"
                  />
                </div>
                <div
                  v-if="
                    subsection.permission &&
                    !subsection.permission.loading.value &&
                    !subsection.permission.hasPermission.value
                  "
                  class="absolute pointer-events-none bg-navy-50 rounded-full text-white"
                  :class="[
                    shrink
                      ? 'right-1 bottom-1 p-0.5'
                      : 'right-3 top-1/2 -translate-y-1/2 p-1',
                  ]"
                >
                  <StellaIcon icon="lock" size="2xs" />
                </div>
              </li>
            </ul>
          </li>
        </ul>
        <JoinAd
          v-if="
            (!user.isLoggedIn ||
              (designer?.stripeCustomerId &&
                referralPermission.hasPermission.value)) &&
            !shrink
          "
          :type="!user.isLoggedIn ? 'join' : 'referral'"
          size="sm"
          class="m-4"
          trackingComponent="Side Nav"
        />
        <div
          v-else-if="messageLinkText?.length && !shrink"
          class="w-full h-fit shrink-0 px-4"
        >
          <StellaText type="Label XS">
            {{ messageText }}
          </StellaText>
          <br />
          <StellaText
            type="Link XS"
            class="cursor-pointer"
            @click="messageLinkAction"
          >
            {{ messageLinkText }}
          </StellaText>
        </div>
        <div
          class="w-full z-10 h-4 from-grey-10 sticky shrink-0 pointer-events-none"
          :class="[
            bottomShadow.state ? 'bg-gradient-to-t' : 'bg-transparent',
            shouldDisplayDesigner ? 'bottom-16' : 'bottom-0',
          ]"
        />
        <template v-if="shouldDisplayDesigner">
          <Spacer />
          <div
            class="w-full py-4 shrink-0 flex items-center sticky bottom-0 bg-cream-10 px-2"
          >
            <StellaSpinner v-if="loadingDesigner" />
            <router-link
              v-else-if="designer"
              :to="{ name: 'profile', params: { nickname: designer.nickname } }"
            >
              <div
                class="flex flex-row items-center justify-center gap-2 px-4 py-0.5"
              >
                <StellaTooltip text="View profile">
                  <DesignerImage
                    class="w-8 h-8 flex-shrink-0 rounded-full overflow-hidden"
                    :designer="designer"
                    bgClass="bg-cream-40"
                  />
                  <template v-if="!shrink">
                    <StellaText type="Label S" class="line-clamp-2">
                      {{ designer.firstName }} {{ designer.lastName }}
                    </StellaText>
                  </template>
                </StellaTooltip>
              </div>
            </router-link>
            <Spacer />
            <StellaTooltip v-if="!shrink" text="Settings">
              <StellaLink :to="{ name: 'settings' }">
                <StellaButton
                  size="sm"
                  type="ghost-secondary"
                  icon="settings"
                />
              </StellaLink>
            </StellaTooltip>
          </div>
        </template>
      </StellaEdgeObserver>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent } from 'vue'
import logo from '/@/assets/logo/spoak-stella.svg'
import { useRoute, type RouteLocationAsRelativeGeneric } from 'vue-router'
import { useTracker } from '/@/core/tracking/api'
import { useCurrentDesigner } from '/@/data/designer/current'
import { useAccountsData } from '/@/common/accounts/api'
import { usePermission, useTierUpgrade } from '/@/common/tiers'
import { type StellaIconId } from '/@/stella/icons/types'
import { useUser } from '/@/data/user'
import { useConfig } from '/@/core/config'
import { useToast } from '/@/core/elements/toast'
import { useDesignerApi } from '/@/data/designer/api'
import { useLoading } from '/@/core/elements/loading'
import { useToggle } from '/@/utils/misc'
import { formattedDate } from '/@/utils/date'
import { stellaModal } from '/@/stella/StellaModal'
import { type ProjectModalTabType } from '/@/common/projects/ProjectModal'
import { type MockupType } from '/@/common/viz/types'
import { useDevice } from '/@/utils/responsive'
import { useInternalSignalApi } from '/@/common/internalSignal/api'
import DesignerImage from '/@/common/DesignerImage.vue'
import { createReusableTemplate } from '@vueuse/core'
import JoinAd from '/@/common/JoinAd.vue'

const { inline, shrink } = defineProps<{
  inline?: boolean
  shrink?: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

const user = useUser()
const config = useConfig()
const route = useRoute()
const tracker = useTracker()
const device = useDevice()
const Tpl = createReusableTemplate<{
  subsection: Subsection
  active?: boolean
  disabled?: boolean
  iconRight?: StellaIconId
}>({
  inheritAttrs: false,
})

const { designer, isOrgMember, loading: loadingDesigner } = useCurrentDesigner()
const { membershipSubscription, isMembershipSubscriptionCanceling } =
  useAccountsData()

const toast = useToast()
const designerApi = useDesignerApi()
const loading = useLoading()
const referralPermission = usePermission('REFERRAL_LINKS')
const createViziPermission = usePermission('CREATE_VIZIS')
const createProjectPermission = usePermission('CREATE_PROJECTS')
const createPalettePermission = usePermission('CREATE_PALETTES')
const designShopPermission = usePermission('DESIGN_SHOP')
const clientsPermission = usePermission('CLIENTS')
const jobFairPermission = usePermission('JOB_FAIR')
const { navigateToTierUpgrade } = useTierUpgrade()
const topShadow = useToggle()
const bottomShadow = useToggle()
const internalSignalApi = useInternalSignalApi()

interface BlankSubsection {
  id: string
  label: string
  iconId?: StellaIconId
  permission?: ReturnType<typeof usePermission>
}

interface RouteSubsection extends BlankSubsection {
  route: RouteLocationAsRelativeGeneric
  action?: () => void
}

interface LinkSubsection extends BlankSubsection {
  url: string
  action?: () => void
}

interface ActionSubsection extends BlankSubsection {
  action: () => void
}

type Subsection =
  | RouteSubsection
  | LinkSubsection
  | ActionSubsection
  | BlankSubsection

type Section = {
  id: string
  subsections: Subsection[]
  loading?: boolean
}

const shouldDisplayDesigner = computed(
  () => (loadingDesigner.value || designer.value) && !isOrgMember.value,
)

const isAdmin = computed(() => user.current?.isAdmin)

const navSections = computed(() => {
  const sections: Section[] = []

  sections.push({
    id: 'Home',
    subsections: [
      {
        id: 'home',
        label: 'Home',
        iconId: 'home',
        route: { name: 'home' },
      },
    ],
  })

  sections.push({
    id: 'Designer Things',
    subsections: [
      {
        id: 'my things',
        label: 'My Things',
        iconId: 'bookmark-empty',
        route: { name: 'my-things' },
      },
      {
        id: 'source',
        label: 'Source',
        iconId: 'search',
        route: { name: 'source' },
      },
    ],
  })

  sections.push({
    id: 'Tools',
    subsections: [
      {
        id: 'my designs',
        label: 'My Designs',
        iconId: 'grid_view',
        route: { name: 'designs' },
      },
      {
        id: 'open-vizi',
        label: 'Viz',
        iconId: 'vizualize',
        action: () => {
          openVizis([])
        },
        permission: createViziPermission,
      },
      {
        id: 'open-project',
        label: 'Project Hub',
        iconId: 'folder',
        action: () => {
          openProjects()
        },
        permission: createProjectPermission,
      },
      {
        id: 'open-pal',
        label: 'Color Pal',
        iconId: 'palette',
        action: () => {
          openColorPals()
        },
        permission: createPalettePermission,
      },
    ],
  })

  const featuresSubsections: Subsection[] = [
    {
      id: 'school',
      label: 'Classes & Tips',
      iconId: 'school',
      route: { name: 'learn' },
    },
  ]
  if (!isOrgMember.value) {
    featuresSubsections.push({
      id: 'community',
      label: 'Community Forum',
      iconId: 'chat bubble',
      url: 'https://community.spoak.com',
    })
  }
  sections.push({
    id: 'Features',
    subsections: featuresSubsections,
  })

  if (!isOrgMember.value) {
    const businessSubsections: Subsection[] = []

    if (designer.value) {
      businessSubsections.push(
        {
          id: 'shop',
          label: 'Design Shop',
          iconId: 'design-shop',
          route: {
            name: 'profile',
            query: { t: 'services' },
            params: { nickname: designer.value.nickname },
          },
          permission: designShopPermission,
        },
        {
          id: 'client',
          label: 'Clients & Payments',
          iconId: 'clients-payments',
          route: { name: 'services', query: { t: 'payments' } },
          permission: clientsPermission,
        },
      )
    }

    businessSubsections.push({
      id: 'job',
      label: 'Job fair',
      iconId: 'briefcase',
      route: { name: 'jobs' },
      permission: jobFairPermission,
    })

    sections.push({
      id: 'Business',
      subsections: businessSubsections,
    })
  }

  sections.push({
    id: 'Help',
    subsections: [
      {
        id: 'help',
        label: 'Help & support',
        iconId: 'questionmark-circle',
        url: 'https://help.spoak.com/',
        action: getHelpClicked,
      },
    ],
  })

  if (config.env !== 'prod') {
    sections.push({
      id: 'Dev',
      subsections: [
        {
          id: 'dev',
          label: 'Dev',
          iconId: 'lightning',
          route: { name: 'dev-index' },
        },
      ],
    })
  }

  if (isAdmin.value) {
    sections.push({
      id: 'Admin',
      subsections: [
        {
          id: 'index',
          label: 'Admin index',
          iconId: 'command',
          url: `${config.urls.admin}/admin`,
        },
        {
          id: 'version',
          iconId: 'info',
          label: `Version: ${config.version}`,
        },
      ],
    })
  }

  return sections.filter(
    section => section.loading || section.subsections?.length,
  )
})

const messageText = computed(() => {
  if (isMembershipSubscriptionCanceling.value) {
    if (membershipSubscription.value?.cancelAt) {
      return `Your Spoak membership will end on ${formattedDate(
        membershipSubscription.value.cancelAt,
      )}`
    } else {
      return 'Not ready to leave your creative side behind?'
    }
  } else if (designer.value?.tier === 'FREE' || !designer.value) {
    return 'Unlock even more features.'
  } else {
    return undefined
  }
})
const messageLinkText = computed(() => {
  if (isMembershipSubscriptionCanceling.value) {
    return 'Keep membership'
  } else if (designer.value?.tier === 'FREE' || !designer.value) {
    return designer.value ? 'Upgrade now' : 'Become a member'
  } else {
    return undefined
  }
})
const messageLinkAction = computed(() => {
  if (isMembershipSubscriptionCanceling.value) {
    return stopCancelation
  } else if (designer.value?.tier === 'FREE' || !designer.value) {
    return () => {
      navigateToTierUpgrade({ source: 'Side Nav' })
    }
  } else {
    return undefined
  }
})

async function stopCancelation() {
  loading.show()
  await designerApi.stopCancelation()
  loading.hide()
  toast.show({
    type: 'stella',
    message: 'Yay! Welcome back. Your cancelation has been…canceled.',
    severity: 'success',
  })
}

function openProjects() {
  tracker.send('Projects Viewed', {
    component: 'Side Nav',
  })

  openProjectModal('existing project')
}

function openProjectModal(initialTab?: ProjectModalTabType) {
  stellaModal.open({
    content: defineAsyncComponent(
      () => import('/@/common/projects/ProjectModal/ProjectModalContent.vue'),
    ),
    data: {
      initialTab,
    },
  })
}

async function openVizis(viziTypes: MockupType[]) {
  tracker.send('Vizis Viewed', {
    component: 'Side Nav',
    isPhone: device.isSm,
  })

  stellaModal.open({
    content: defineAsyncComponent(
      () => import('/@/common/viz/modals/VizisModalContent.vue'),
    ),
    data: {
      viziTypes,
    },
  })
}

function openColorPals() {
  tracker.send('Color Pals Viewed', {
    component: 'Side Nav',
  })

  stellaModal.open({
    content: defineAsyncComponent(
      () => import('/@/common/modals/PalettesModal/PalettesModalContent.vue'),
    ),
  })
}

async function getHelpClicked() {
  await internalSignalApi.trackEvent({ eventType: 'HELP_HUB_CLICKED' })
}
</script>

<style scoped lang="scss"></style>
